// Tenantra Inc. - Public rental application (screening) opened from a property's link
import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, ArrowLeft, ArrowRight, CheckCircle2, Loader2, MapPin } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { Checkbox } from '@/components/ui/checkbox';
import api from '@/services/api';
import { AboutStep, AddressStep, WorkStep, HouseholdStep, ReferenceStep, DocumentsStep, CoApplicantsStep, blankAddress, STATUS_LABEL } from '@/components/apply/ApplySteps';

const STEPS = ['About you', 'Addresses', 'Work & income', 'Household', 'References', 'Documents', 'Co-applicants', 'Review'];
const STEP_DESC = [
  'Your legal name and contact details.',
  'Where you live now, and before if under two years here.',
  'Your job and income.',
  'Children and your move-in date.',
  'One reference and an emergency contact.',
  'Upload clear PDFs or photos.',
  'Anyone else 18 or older who will share the rent. They fill the same application, without the Equifax report.',
  'Check your details and submit.',
];
const LAST = STEPS.length - 1;
const age = (d) => {
  if (!d) return -1;
  const [y, m, day] = d.split('-').map(Number); const t = new Date();
  return t.getFullYear() - y - ((t.getMonth() + 1 < m || (t.getMonth() + 1 === m && t.getDate() < day)) ? 1 : 0);
};
const today = () => { const t = new Date(); return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`; };
const errText = (err) => { const d = err?.response?.data?.detail; return typeof d === 'string' ? d : Array.isArray(d) ? d.map(x => x?.msg).join(' ') : 'Something went wrong. Please try again.'; };
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const email_ok = (e) => /^\S+@\S+\.\S+$/.test(e || '');
const phone_ok = (p) => (p || '').replace(/\D/g, '').length >= 10;
const currentMonths = (list) => (Number(list[0]?.years) || 0) * 12 + (Number(list[0]?.months) || 0);
const docCount = (docs, t) => docs.filter((d) => d.doc_type === t).length;

const vAbout = (s) => {
  if (!s.first_name.trim() || !s.last_name.trim()) return 'Enter the legal first and last name';
  if (age(s.date_of_birth) < 18) return 'Everyone on the application must be 18 or older';
  if (age(s.date_of_birth) > 120) return 'Check the date of birth';
  if (!email_ok(s.email)) return 'Enter a valid email';
  if (!phone_ok(s.phone)) return 'Enter a 10-digit phone number';
  return '';
};
const vAddresses = (s) => {
  for (const a of s.addresses) {
    if (!a.street.trim() || !a.city.trim() || !a.region.trim() || !a.postal_code.trim() || !a.country.trim()) return 'Fill in every address field';
    if (a.street.trim().length < 3 || a.city.trim().length < 2 || a.region.trim().length < 2 || a.postal_code.trim().length < 3 || a.country.trim().length < 2) return `Check the address details for ${a.street || 'each address'}`;
    if (a.country === 'Canada' && !/^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(a.postal_code.trim())) return `Check the postal code for ${a.street}`;
    if (a.years === '' || a.months === '') return 'Enter how long you lived at each address';
    if (a.landlord_email && !email_ok(a.landlord_email)) return `Check the landlord email for ${a.street}`;
  }
  if (currentMonths(s.addresses) < 24 && s.addresses.length < 2) return 'Under 2 years at the current address. Add the previous address.';
  return '';
};
const vWork = (s) => {
  const w = s.employment;
  if (!w.status) return 'Choose a work status';
  if (['full_time', 'part_time'].includes(w.status)) {
    if (!w.employer.trim() || !w.job_title.trim() || w.years === '' || w.months === '') return 'Enter the employer, job title and how long worked there';
    if (!w.gross_annual_income || Number(w.gross_annual_income) <= 0) return 'Enter the gross annual income';
    if (!w.pay_frequency) return 'Select how often paid';
  }
  return '';
};
const vReference = (s) => {
  const r = s.reference;
  if (!r.name.trim()) return 'Enter the reference\u2019s name';
  if (r.kind === 'personal' && !r.relationship.trim()) return 'Tell us how you know the reference';
  if (!phone_ok(r.phone)) return 'Enter a 10-digit phone number for the reference';
  if (!email_ok(r.email)) return 'Enter a valid email for the reference';
  const ec = s.emergency_contact;
  if (!ec.name.trim() || !ec.relationship.trim()) return 'Enter the emergency contact name and relationship';
  if (!phone_ok(ec.phone)) return 'Enter a 10-digit phone number for the emergency contact';
  if (ec.email && !email_ok(ec.email)) return 'Check the emergency contact email';
  return '';
};
const vDocs = (s, equifax) => {
  if (docCount(s.documents, 'paystub') < 2) return 'Upload the 2 most recent pay stubs';
  if (docCount(s.documents, 'bank_statement') < 1) return 'Upload a recent bank statement';
  if (equifax && docCount(s.documents, 'equifax_report') < 1) return 'Upload your Equifax credit report';
  return '';
};

function check(step, s) {
  if (step === 0) return vAbout(s);
  if (step === 1) return vAddresses(s);
  if (step === 2) return vWork(s);
  if (step === 3) {
    if (!s.move_in_date || s.move_in_date < today()) return 'Choose a move-in date that is today or later';
    if (s.minors.some(m => !m.full_name.trim() || !m.date_of_birth)) return "Enter each child's name and date of birth";
    if (s.minors.some(m => age(m.date_of_birth) >= 18)) return 'Children listed here must be under 18';
    if (s.pets.has_pets && s.pets.pets.some(p => !p.pet_type.trim())) return 'Tell us the type of each pet';
  }
  if (step === 4) return vReference(s);
  if (step === 5) return vDocs(s, true);
  if (step === 6) {
    for (let i = 0; i < s.co_applicants.length; i++) {
      const co = s.co_applicants[i];
      const who = [co.first_name, co.last_name].filter(Boolean).join(' ') || `Co-applicant ${i + 1}`;
      const m = vAbout(co) || vAddresses(co) || vWork(co) || vReference(co) || vDocs(co, co.leaseholder);
      if (m) return `${who}: ${m}`;
    }
  }
  if (step === LAST && (!s.consent_screening || !s.consent_responsibility)) return 'Please agree to both statements to submit';
  return '';
}

const mapAddresses = (addresses) => addresses.map(({ outside, ...a }) => ({
  ...a, unit: a.unit || null, years: Number(a.years), months: Number(a.months),
  landlord_name: a.landlord_name || null, landlord_phone: a.landlord_phone || null, landlord_email: a.landlord_email || null,
  monthly_rent: a.monthly_rent === '' || a.monthly_rent == null ? null : Number(a.monthly_rent), reason_for_leaving: a.reason_for_leaving || null,
}));
const mapEmployment = (w) => (['full_time', 'part_time'].includes(w.status) ? {
  status: w.status, employer: w.employer, job_title: w.job_title, years: Number(w.years), months: Number(w.months),
  gross_annual_income: w.gross_annual_income === '' ? null : Number(w.gross_annual_income), pay_frequency: w.pay_frequency || null,
  supervisor_name: w.supervisor_name || null, employer_phone: w.employer_phone || null,
  previous: (w.previous && w.previous.employer) ? { employer: w.previous.employer, job_title: w.previous.job_title || null, years: Number(w.previous.years || 0), months: Number(w.previous.months || 0) } : null,
} : { status: w.status });
const mapReference = (r) => ({ kind: r.kind, name: r.name, relationship: r.kind === 'personal' ? (r.relationship || null) : null, phone: r.phone, email: r.email });
const mapEmergency = (ec) => ({ name: ec.name, relationship: ec.relationship, phone: ec.phone, email: ec.email || null });

const payload = (s) => ({
  first_name: s.first_name, middle_name: s.middle_name || null, last_name: s.last_name, date_of_birth: s.date_of_birth,
  email: s.email, phone: s.phone, minors: s.minors.map(m => ({ full_name: m.full_name, date_of_birth: m.date_of_birth })), move_in_date: s.move_in_date,
  consent_screening: s.consent_screening, consent_responsibility: s.consent_responsibility,
  addresses: mapAddresses(s.addresses),
  employment: mapEmployment(s.employment),
  reference: mapReference(s.reference),
  emergency_contact: mapEmergency(s.emergency_contact),
  pets: { has_pets: s.pets.has_pets, pets: s.pets.has_pets ? s.pets.pets.filter(p => p.pet_type.trim()).map(p => ({ pet_type: p.pet_type, breed: p.breed || null, size: p.size || null })) : [] },
  documents: s.documents,
  co_applicants: s.co_applicants.map(co => ({
    first_name: co.first_name, middle_name: co.middle_name || null, last_name: co.last_name, date_of_birth: co.date_of_birth,
    email: co.email, phone: co.phone, relationship: co.relationship, leaseholder: co.leaseholder,
    addresses: mapAddresses(co.addresses),
    employment: mapEmployment(co.employment),
    reference: mapReference(co.reference),
    emergency_contact: mapEmergency(co.emergency_contact),
    documents: co.documents,
  })),
});

function Review({ s, info, set }) {
  const Row = ({ k, v }) => <div className="flex justify-between gap-4 py-1.5 text-sm"><span className="text-stone-500">{k}</span><span className="text-stone-900 text-right">{v}</span></div>;
  const income = s.employment.gross_annual_income ? `$${Number(s.employment.gross_annual_income).toLocaleString()}/yr` : '—';
  return (
    <div className="space-y-5" data-testid="apply-step-review">
      <div className="divide-y divide-stone-100 border border-stone-200 rounded-xl px-5 bg-white shadow-sm">
        <Row k="Applicant" v={`${s.first_name} ${s.middle_name ? s.middle_name + ' ' : ''}${s.last_name}`} />
        <Row k="Addresses" v={`${s.addresses.length} on file`} />
        <Row k="Work" v={STATUS_LABEL[s.employment.status]} />
        <Row k="Gross income" v={income} />
        <Row k="Household" v={`You${s.co_applicants.length ? ` + ${s.co_applicants.length} co-applicant${s.co_applicants.length > 1 ? 's' : ''}` : ''}${s.minors.length ? `, ${s.minors.length} under 18` : ''}`} />
        {s.co_applicants.length > 0 && <Row k="Co-applicants" v={s.co_applicants.map(co => [co.first_name, co.last_name].filter(Boolean).join(' ') || 'Unnamed').join(', ')} />}
        <Row k="Pets" v={s.pets.has_pets ? `${s.pets.pets.filter(p => p.pet_type.trim()).length} pet(s)` : 'None'} />
        <Row k="Reference" v={s.reference.name || '—'} />
        <Row k="Documents" v={`${s.documents.length} uploaded`} />
        <Row k="Move-in" v={s.move_in_date} />
        <Row k="Application fee" v={info.application_fee > 0 ? money(info.application_fee) : 'None'} />
      </div>
      <label className="flex items-start gap-3 cursor-pointer" data-testid="apply-consent-screening">
        <Checkbox checked={s.consent_screening} onCheckedChange={(c) => set({ consent_screening: !!c })} className="mt-0.5" data-testid="apply-consent-screening-box" />
        <span className="text-sm text-stone-700">{info.consent_text}</span>
      </label>
      <label className="flex items-start gap-3 cursor-pointer" data-testid="apply-consent-responsibility">
        <Checkbox checked={s.consent_responsibility} onCheckedChange={(c) => set({ consent_responsibility: !!c })} className="mt-0.5" data-testid="apply-consent-responsibility-box" />
        <span className="text-sm text-stone-700">{info.responsibility_text}</span>
      </label>
      {info.application_fee > 0 && <p className="text-xs text-stone-500 bg-stone-50 border border-stone-200 rounded-md px-3 py-2" data-testid="apply-fee-note">The {money(info.application_fee)} application fee includes your screening. Payment is simulated while card payments are being connected.</p>}
    </div>
  );
}

const initial = () => ({
  first_name: '', middle_name: '', last_name: '', date_of_birth: '', email: '', phone: '',
  addresses: [blankAddress()],
  employment: { status: '', employer: '', job_title: '', years: '', months: '', gross_annual_income: '', pay_frequency: '', supervisor_name: '', employer_phone: '', previous: { employer: '', job_title: '', years: '', months: '' } },
  reference: { kind: 'current_landlord', name: '', relationship: '', phone: '', email: '' },
  emergency_contact: { name: '', relationship: '', phone: '', email: '' },
  pets: { has_pets: false, pets: [] },
  documents: [],
  co_applicants: [], minors: [], move_in_date: '', consent_screening: false, consent_responsibility: false,
});

const DRAFT_PREFIX = 'tenantra_apply_draft_';
const loadDraft = (token) => { try { const r = localStorage.getItem(DRAFT_PREFIX + token); return r ? JSON.parse(r) : null; } catch { return null; } };
const meaningfulDraft = (d) => !!(d && (d.step > 0 || (d.s && (d.s.first_name?.trim() || (d.s.documents || []).length || (d.s.co_applicants || []).length))));

export default function ApplyPage() {
  const token = window.location.pathname.split('/').pop();
  const [draft] = useState(() => loadDraft(token));
  const [info, setInfo] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [step, setStep] = useState(draft?.step ?? 0);
  const [s, setS] = useState(() => draft?.s ?? initial());
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [resumed, setResumed] = useState(() => meaningfulDraft(draft));
  const [savedAt, setSavedAt] = useState(draft?.savedAt || null);
  useEffect(() => { api.get(`/screening/apply/${token}`).then(r => setInfo(r.data)).catch(e => setLoadErr(errText(e))); }, [token]);
  useEffect(() => {
    if (done) return;
    try { localStorage.setItem(DRAFT_PREFIX + token, JSON.stringify({ s, step, savedAt: Date.now() })); setSavedAt(Date.now()); } catch { /* storage full/blocked */ }
  }, [s, step, token, done]);

  const startOver = () => { try { localStorage.removeItem(DRAFT_PREFIX + token); } catch { /* ignore */ } setS(initial()); setStep(0); setError(''); setResumed(false); setSavedAt(null); window.scrollTo(0, 0); };
  const set = (p) => { setS(x => ({ ...x, ...p })); setError(''); };
  const setDocs = (docs) => setS(x => ({ ...x, documents: typeof docs === 'function' ? docs(x.documents) : docs }));
  const next = async () => {
    const msg = check(step, s); if (msg) { setError(msg); setTimeout(() => document.querySelector('[data-testid=apply-error]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50); return; }
    if (step < LAST) { setStep(step + 1); window.scrollTo(0, 0); return; }
    setBusy(true);
    try { const r = await api.post(`/screening/apply/${token}`, payload(s)); try { localStorage.removeItem(DRAFT_PREFIX + token); } catch { /* ignore */ } setDone(r.data); window.scrollTo(0, 0); }
    catch (e) { setError(errText(e)); setTimeout(() => document.querySelector('[data-testid=apply-error]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50); } finally { setBusy(false); }
  };

  const pct = Math.round(((step + 1) / STEPS.length) * 100);
  const shell = (children, bar = false) => (
    <div className="apply-type min-h-screen bg-white">
      <header className="bg-white/90 backdrop-blur border-b border-stone-200 sticky top-0 z-20">
        <div className="max-w-2xl mx-auto px-5 h-14 flex items-center"><Logo /></div>
        {bar && <div className="h-1 bg-stone-100"><motion.div className="h-full bg-stone-800" initial={false} animate={{ width: `${pct}%` }} transition={{ type: 'spring', stiffness: 120, damping: 20 }} /></div>}
      </header>
      <main className="max-w-2xl mx-auto px-5 py-10 sm:py-14">{children}</main>
    </div>
  );
  if (loadErr) return shell(<div className="bg-white border border-stone-200 rounded-2xl shadow-sm p-8 text-center" data-testid="apply-link-error"><AlertCircle className="w-6 h-6 text-stone-400 mx-auto mb-2" /><p className="text-sm text-stone-700">{loadErr}</p></div>);
  if (!info) return shell(<div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>);
  const p = info.property;
  if (done) return shell(
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-stone-200 rounded-2xl shadow-sm p-10 text-center" data-testid="apply-success">
      <div className="w-14 h-14 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="w-7 h-7 text-teal-600" /></div>
      <h1 className="text-2xl font-bold tracking-tight text-stone-900">Application submitted</h1>
      <p className="text-sm text-stone-600 mt-2">Reference <span className="font-mono font-semibold text-stone-900" data-testid="apply-reference">{done.reference}</span></p>
      <p className="text-[15px] leading-relaxed text-stone-600 mt-5 max-w-md mx-auto">{info.manager_name} will review your application for {p.street}, {p.city}. If you're selected, you'll get an email to finish onboarding. That's when you'll verify your ID.</p>
    </motion.div>
  );

  return shell(
    <div data-testid="apply-page">
      {resumed && (
        <div className="mb-5 flex items-center gap-3 rounded-md border border-stone-300 bg-stone-50 px-4 py-3" data-testid="apply-resumed">
          <CheckCircle2 className="w-4 h-4 text-stone-700 flex-shrink-0" />
          <p className="text-sm text-stone-800 flex-1">We saved your progress on this device, so you're back where you left off.</p>
          <button type="button" onClick={startOver} className="text-sm font-medium text-stone-900 hover:text-stone-700 underline underline-offset-2 flex-shrink-0" data-testid="apply-start-over">Start over</button>
        </div>
      )}
      <div className="mb-8">
        <p className="text-sm text-stone-800 flex items-center gap-1.5 mb-3"><MapPin className="w-4 h-4 text-stone-700" /> {p.street}, {p.city}, {p.province}</p>
        <div className="flex items-center justify-between text-sm font-medium mb-2">
          <span className="text-stone-800">Step {step + 1} of {STEPS.length}</span>
          <span className="text-stone-800 flex items-center gap-2">{savedAt && <span className="inline-flex items-center gap-1 text-stone-700" data-testid="apply-saved"><CheckCircle2 className="w-3.5 h-3.5" /> Saved</span>}{pct}% complete</span>
        </div>
        <div className="h-1.5 rounded-full bg-stone-200 overflow-hidden">
          <motion.div className="h-full rounded-full bg-stone-800" initial={false} animate={{ width: `${pct}%` }} transition={{ type: 'spring', stiffness: 120, damping: 20 }} />
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}>
          <h1 className="text-[1.75rem] font-bold tracking-tight text-stone-900 leading-tight">{STEPS[step]}</h1>
          <p className="text-base text-stone-900 mt-2 mb-6">{STEP_DESC[step]}</p>
          <div className="bg-white border border-stone-300 rounded-md p-5 sm:p-7">
            {step === 0 && <AboutStep v={s} set={set} />}
            {step === 1 && <AddressStep list={s.addresses} setList={(addresses) => set({ addresses })} />}
            {step === 2 && <WorkStep v={s.employment} set={(x) => set({ employment: { ...s.employment, ...x } })} />}
            {step === 3 && <HouseholdStep v={s} set={set} />}
            {step === 4 && <ReferenceStep v={s} set={set} />}
            {step === 5 && <DocumentsStep token={token} docs={s.documents} setDocs={setDocs} />}
            {step === 6 && <CoApplicantsStep list={s.co_applicants} setList={(co_applicants) => set({ co_applicants })} token={token} />}
            {step === LAST && <Review s={s} info={info} set={set} />}
          </div>
        </motion.div>
      </AnimatePresence>

      {error && <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-600 text-sm" data-testid="apply-error"><AlertCircle className="w-4 h-4 flex-shrink-0" />{error}</div>}
      <div className="sticky bottom-0 -mx-5 px-5 py-3 mt-8 bg-gradient-to-t from-white via-white/95 to-white/80 backdrop-blur border-t border-stone-200 sm:static sm:mx-0 sm:px-0 sm:py-0 sm:mt-10 sm:bg-transparent sm:border-0 sm:backdrop-blur-none flex items-center justify-between gap-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pb-0">
        <button type="button" onClick={() => { setStep(step - 1); setError(''); window.scrollTo(0, 0); }} disabled={step === 0} className={`h-12 px-4 text-sm font-medium text-stone-500 hover:text-stone-800 transition-colors inline-flex items-center gap-1.5 ${step === 0 ? 'invisible' : ''}`} data-testid="apply-back"><ArrowLeft className="w-4 h-4" /> Back</button>
        <button type="button" onClick={next} disabled={busy} className="flex-1 sm:flex-none justify-center h-12 px-7 rounded-xl active:scale-[0.98] transition-all bg-teal-600 hover:bg-teal-700 hover:shadow-lg hover:shadow-teal-600/20 text-white text-[15px] font-semibold inline-flex items-center gap-2 disabled:opacity-50 shadow-md shadow-teal-600/10" data-testid="apply-next">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}{step < LAST ? 'Continue' : info.application_fee > 0 ? `Pay ${money(info.application_fee)} and submit` : 'Submit application'}{!busy && step < LAST && <ArrowRight className="w-4 h-4" />}
        </button>
      </div>
    </div>, true
  );
}
